import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import AllInboxIcon from "@material-ui/icons/AllInbox";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Divider, Popover, Space, Switch, Tooltip, Typography } from "antd";
import axios from "axios";
import { selectChartMultiProductFocusFeature, updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { findCatalogColumn, getMaxBinDefault } from "utils/groupBy/maxBinDefaults";

import { useUserEvents } from "hooks";
import { useChartEntities } from "hooks/charts";
import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import { BinType, IGroupBy, QuantileState } from "models";
import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { IconButton } from "components/charts/components/IconButton";
import { BinSettings } from "components/filter";
import { ColorPaletteListToggle } from "components/filter/focus";
import GroupBy from "components/groupBy";
import { PolymerInjection } from "components/icons";
import ProductBoe6 from "components/icons/Icon_ProductBoe6";
import TotalFluid from "components/icons/TotalFluid";
import { IColorPalette } from "components/user-settings/models";
import { useUserDefaults } from "components/user/hooks";

import { WellOperatorGroupBy } from "../../../../constants";
import { EvaChart } from "../../../../constants/charts.enums";
import { useChartsContext } from "../../../../contexts";

const BIN_SIZE_FOCUS_QUERY_KEY = "bin-size";

const { Text } = Typography;
const dataRoot = process.env.REACT_APP_DATA_RS_ROOT;

const ChartFocusSettings = () => {
  const { id, entityKind } = useChartsContext();
  const dispatch = useDispatch();
  const userDefaults = useUserDefaults();
  const { sendChartFocusSetEvent } = useUserEvents();
  const { chartType, chartFocus } = useChartEntities(id);

  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const globalFacilityGroupBy = useSelector(
    (state: RootState) => state.groupBy.globalFacilityFocus
  );

  const globalPalette = useSelector(
    (state: RootState) => state.userSetting.activeColorPalette
  ) as IColorPalette;

  const chartFocusGroupBy = chartFocus?.properties?.groupBy;
  const bin = chartFocus?.properties?.groupBy?.bin;
  const colorPalette = chartFocus?.properties?.colorPalette;
  const isChartFocusActive = chartFocus?.active;

  const isWellContributionChart = chartType === EvaChart.WellContribution;
  const focusEntities = isWellContributionChart ? EntityKind.Well : entityKind;
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well || isWellContributionChart //well contribution chart uses Well groupings for focus
      ? state.filter.filterId
      : state.facilityFilter.filterId
  );
  const [localBin, setLocalBin] = useState<mBinSize>(
    isChartFocusActive ? bin : globalGroupBy.bin
  );
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  // Set by a selection (handleFocusChange) or Reset Default, read once by the
  // next bin-size onSuccess. Its absence (a popover reopen remount, not a
  // selection) is what tells onSuccess to carry MaxBins through instead of
  // dropping it (EVA-306 Risk R2).
  const pendingDefaultRef = useRef<Maybe<{ property: string }>>(undefined);

  const multiProductFocusFeature = useSelector((state: RootState) =>
    selectChartMultiProductFocusFeature(state, id)
  );
  const isTotalRateDateChart = chartType === EvaChart.TotalRateDate;
  const isMultiProductFocusActive = multiProductFocusFeature?.active;

  const getChartFocusText = () => {
    switch (chartType) {
      case EvaChart.WellContribution:
        return "Upstream Selected Field";
      default:
        return "Chart Focus";
    }
  };

  const handleChartFocusToggle = (active: boolean) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartFocus.active",
        value: active
      })
    );
    setLocalBin({
      ...localBin,
      UseDynamicBins: userDefaults.binSettings?.useDynamicBins ?? false
    });
  };
  const handleGroupByChange = (groupBy: Partial<IGroupBy>) => {
    dispatch(
      updateChartByPath({
        id,
        path: `features.chartFocus.properties.groupBy`,
        value: groupBy
      })
    );
  };

  function handleLocalBinChange(val: mBinSize) {
    const newBin = Object.assign({}, localBin, val);
    setLocalBin(newBin);
  }

  const handleColorPaletteChange = (colorPalette: Partial<IColorPalette>) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartFocus.properties.colorPalette",
        value: colorPalette
      })
    );
  };

  // Records whether the palette above is the user's own choice — see
  // colorPaletteIsExplicit on ChartFocusFeature.
  const markColorPalettePicked = (picked: boolean) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartFocus.properties.colorPaletteIsExplicit",
        value: picked
      })
    );
  };

  const handleColorPaletteSelected = (colorPalette: Partial<IColorPalette>) => {
    handleColorPaletteChange(colorPalette);
    markColorPalettePicked(true);
  };

  const handleFocusChange = (
    groupBy,
    isForecastToggleOn,
    selectedForecastFolder,
    selectedForecastFolderName,
    selectedPdenSource,
    performanceDateRange?
  ) => {
    // Only a real field switch schedules a default resolution — re-picking
    // the same field dispatches but never changes chartFocusGroupBy.property,
    // so no refetch follows it. Leaving the ref set here would make it stick
    // around for whatever unrelated refetch runs next (e.g. a filterId
    // change) and stomp that refetch's MaxBins with the catalog default.
    if (groupBy.property !== chartFocusGroupBy.property) {
      pendingDefaultRef.current = { property: groupBy.property };
    }
    sendChartFocusSetEvent({
      chartType: chartType,
      entityKind: entityKind,
      groupBy: groupBy.property,
      globalGroupBy:
        entityKind === EntityKind.Well || isWellContributionChart
          ? globalGroupBy.property
          : globalFacilityGroupBy.property
    });
    setLocalBin({
      ...localBin,
      UseDynamicBins: userDefaults.binSettings?.useDynamicBins ?? false
    });
    handleGroupByChange({
      ...groupBy,
      pdenSource: selectedPdenSource,
      entityKind: focusEntities,
      groupByField: groupBy.property,
      bin: { ...localBin },
      performanceDateRange
    });
  };

  // EVA-306: resolves MaxBins/MaxBinsSortOrder for the bin-size onSuccess handlers.
  // Flag off (Azure): the bin is rebuilt without MaxBins exactly as on main. Flag on:
  // a pending selection/reset (the ref is set) gets the catalog default, or "" for a
  // field without one; the no-pending-ref case (a popover
  // reopen remount refetching the same field, not a selection or reset) carries
  // the current localBin's MaxBins through unchanged (Risk R2 fix). Clears the
  // ref so it only applies once.
  const resolveMaxBinsForBin = useCallback((): Pick<
    mBinSize,
    "MaxBins" | "MaxBinsSortOrder" | "MaxBinsSortField"
  > => {
    const pending = pendingDefaultRef.current;
    pendingDefaultRef.current = undefined;
    const isSelectionOrReset = pending?.property === chartFocusGroupBy.property;
    // The criteria are the user's choice on both clouds, so a reopen keeps them even
    // where MaxBins is dropped.
    if (!maxBinDefaultsEnabled) {
      return isSelectionOrReset
        ? {}
        : {
            MaxBinsSortOrder: localBin.MaxBinsSortOrder,
            MaxBinsSortField: localBin.MaxBinsSortField
          };
    }

    if (isSelectionOrReset) {
      const catalogDefault = getMaxBinDefault(
        findCatalogColumn(categoryList, pending.property),
        focusEntities
      );
      if (catalogDefault != null) {
        return { MaxBins: catalogDefault, MaxBinsSortOrder: "WellCount" };
      }
      return { MaxBins: "" };
    }

    return {
      MaxBins: localBin.MaxBins ?? "",
      MaxBinsSortOrder: localBin.MaxBinsSortOrder ?? "WellCount",
      MaxBinsSortField: localBin.MaxBinsSortField
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    maxBinDefaultsEnabled,
    chartFocusGroupBy.property,
    localBin,
    focusEntities,
    categoryList
  ]);

  const { refetch } = useQuery(
    [BIN_SIZE_FOCUS_QUERY_KEY, id],
    () => {
      const params = new URLSearchParams({
        normalizeField: "''",
        per: "0",
        useDynamicBins: String(localBin?.UseDynamicBins),
        entityKind: focusEntities
      });
      if (chartFocusGroupBy.performanceDateRange) {
        params.set("startDate", chartFocusGroupBy.performanceDateRange.startDate);
        params.set("endDate", chartFocusGroupBy.performanceDateRange.endDate);
      }
      return axios.get(
        `${dataRoot}/api/v1/data/bin-size/${filterId.id}/${btoa(
          encodeURIComponent(chartFocusGroupBy.property)
        )}?${params.toString()}`
      );
    },
    {
      enabled: false,
      // A failed reset must not linger and override a later refetch.
      onError: () => {
        pendingDefaultRef.current = undefined;
      },
      // need to wait for the default bin response
      onSuccess: (data) => {
        if (data?.data?.isValid) {
          // no need to fetch bins if bin type is quantile
          if (localBin?.BinType === "Quantile") {
            // This resolves the selection, so a later refetch must not apply its default.
            pendingDefaultRef.current = undefined;
            return;
          }

          // updates the chart focus for values that can be binned
          const newBin = data.data;
          const binned: mBinSize = {
            LessThan: newBin.lessThan,
            GreaterThan: newBin.greaterThan,
            MinSize: newBin.minSize,
            BinSize: newBin.binSize,
            BinType: "BinSize",
            Quantile: null,
            UseDynamicBins: localBin.UseDynamicBins ?? false,
            ...resolveMaxBinsForBin()
          };

          setLocalBin(binned);
          const groupBy = {
            ...chartFocusGroupBy,
            groupByField: chartFocusGroupBy.property,
            bin: binned
          };
          handleGroupByChange(groupBy);
          handleColorPaletteChange(colorPalette);
        } else {
          // updates the chart focus for values without binning
          let defaultBin: mBinSize = {
            LessThan: localBin.LessThan ?? "",
            GreaterThan: localBin.GreaterThan ?? "",
            MinSize: localBin.MinSize ?? "",
            BinSize: localBin.BinSize ?? "",
            BinType: "BinSize",
            Quantile: null,
            UseDynamicBins: localBin.UseDynamicBins ?? false,
            ...resolveMaxBinsForBin()
          };
          if (
            userDefaults.binSettings?.type === "Quantile" &&
            (chartFocusGroupBy.dataType.toLowerCase() === "number" ||
              chartFocusGroupBy.dataType.toLowerCase() === "integer")
          ) {
            defaultBin = {
              ...defaultBin,
              BinType: "Quantile",
              Quantile: {
                quantileType: userDefaults.binSettings.quantile.quantileType,
                numQuantiles: userDefaults.binSettings.quantile.numQuantiles
              }
            };
          }
          setLocalBin(defaultBin);
          const groupBy = {
            ...chartFocusGroupBy,
            groupByField: chartFocusGroupBy.property,
            bin: defaultBin
          };
          handleGroupByChange(groupBy);
          handleColorPaletteChange(colorPalette);
        }
      }
    }
  );
  function resetQuantileBin() {
    let defaultBin: mBinSize = {
      LessThan: localBin.LessThan ?? "",
      GreaterThan: localBin.GreaterThan ?? "",
      MinSize: localBin.MinSize ?? "",
      BinSize: localBin.BinSize ?? "",
      BinType: "BinSize",
      Quantile: null,
      UseDynamicBins: localBin.UseDynamicBins ?? false
    };
    if (
      localBin.BinType === "Quantile" &&
      (chartFocusGroupBy.dataType.toLowerCase() === "number" ||
        chartFocusGroupBy.dataType.toLowerCase() === "integer")
    ) {
      defaultBin = {
        ...defaultBin,
        BinType: "Quantile",
        Quantile: {
          quantileType: userDefaults.binSettings.quantile.quantileType,
          numQuantiles: userDefaults.binSettings.quantile.numQuantiles
        }
      };
    }
    setLocalBin(defaultBin);
    const groupBy = {
      ...chartFocusGroupBy,
      groupByField: chartFocusGroupBy.property,
      bin: defaultBin
    };
    handleGroupByChange(groupBy);
    handleColorPaletteChange(colorPalette);
  }
  // Reset Default for a non-quantile bin: marks the field as pending so the
  // next onSuccess applies the catalog default instead of carrying
  // through whatever MaxBins the user had set.
  const handleResetDefault = useCallback(() => {
    pendingDefaultRef.current = { property: chartFocusGroupBy.property };
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartFocusGroupBy.property, refetch]);

  function updateBin(binType: BinType, quantile: QuantileState, useDynamicBins: boolean) {
    let newBinType = binType;
    // Show type as dynamic but function as bin size besides useDynamicBins
    if (binType === "Dynamic") {
      newBinType = "BinSize";
    }
    // An explicit save supersedes any in-flight Reset Default.
    pendingDefaultRef.current = undefined;
    const newBin = { ...localBin };
    newBin.BinType = newBinType;
    newBin.Quantile = quantile;
    newBin.UseDynamicBins = useDynamicBins;
    setLocalBin(newBin);
    const updatedFocus = {
      ...chartFocusGroupBy,
      groupByField: chartFocusGroupBy.property,
      bin: newBin
    };
    handleGroupByChange(updatedFocus);
    handleColorPaletteChange(colorPalette);
    updateChartByPath({
      id,
      path: "features.chartFocus.properties.groupBy.bin",
      value: newBin
    });
  }

  // keeps the chart focus the same as the global focus when not being used
  useEffect(() => {
    if (!isChartFocusActive) {
      if (isWellContributionChart) {
        handleGroupByChange(globalGroupBy);
      } else if (entityKind === EntityKind.Facility) {
        handleGroupByChange(globalFacilityGroupBy);
      } else {
        handleGroupByChange(globalGroupBy);
      }
    }
  }, [globalGroupBy, globalFacilityGroupBy, isChartFocusActive, isWellContributionChart]);

  // keeps the chart colour palette the same as the global colour palette
  // when chart focus is not being used
  useEffect(() => {
    if (!isChartFocusActive) {
      handleColorPaletteChange(globalPalette);
      markColorPalettePicked(false);
    }
  }, [globalPalette, isChartFocusActive]);

  // updates the chart focus
  useEffect(() => {
    if (isChartFocusActive) {
      removeOtherProducts();
      // need to obtain/set bin settings when swapping groupBy in chart-specific focus
      refetch();
      return;
    }
    handleGroupByChange(
      isChartFocusActive
        ? //override entityKind to well if well contribution chart
          //because group by is well fields not facility fields
          {
            ...chartFocusGroupBy,
            groupByField: chartFocusGroupBy.property,
            entityKind: focusEntities
          }
        : isWellContributionChart
        ? globalGroupBy // Well contribution charts use well groupBy when not using chart-specific focus
        : entityKind === EntityKind.Well
        ? globalGroupBy
        : globalFacilityGroupBy
    );
  }, [isChartFocusActive, chartFocusGroupBy.property]);

  // refetch bins when switching from dynamic to static bins
  useEffect(() => {
    if (
      isChartFocusActive &&
      // string means user set bins, don't refetch
      (!(
        typeof localBin.BinSize === "string" ||
        typeof localBin.GreaterThan === "string" ||
        typeof localBin.LessThan === "string" ||
        typeof localBin.MinSize === "string"
      ) ||
        localBin.UseDynamicBins)
    ) {
      refetch();
    }
  }, [localBin.UseDynamicBins]);

  // refetch dynamic bins when filterId changes
  useEffect(() => {
    if (isChartFocusActive && localBin.UseDynamicBins) {
      refetch();
    }
  }, [filterId]);

  // set the chart focus to well operator for well contribution chart when chart focus is active,
  // and facility focus when switching back to other midstream charts
  useEffect(() => {
    if (
      isChartFocusActive &&
      isWellContributionChart &&
      // don't set group by back if the previous chart was a well contribution chart
      !(
        chartFocusGroupBy?.groupByField === WellOperatorGroupBy.groupByField ||
        chartFocusGroupBy.entityKind === EntityKind.Well
      )
    ) {
      handleGroupByChange(WellOperatorGroupBy);
    } else if (
      // only set group by back if the previous chart was a well contribution chart
      isChartFocusActive &&
      entityKind === EntityKind.Facility &&
      (chartFocusGroupBy?.groupByField === WellOperatorGroupBy.groupByField ||
        chartFocusGroupBy.entityKind === EntityKind.Well) &&
      !isWellContributionChart
    ) {
      handleGroupByChange(globalFacilityGroupBy);
    }
  }, [isWellContributionChart]);

  const handleMultiProductFocusChange = (products: MultiProducts) => {
    // Turn on the legend when multiproduct focus is enabled and a product is selected
    dispatch(
      updateChartByPath({
        id,
        path: "features.legend.active",
        value: true
      })
    );
    dispatch(
      updateChartByPath({
        id,
        path: "features.multiProductFocus.properties.products",
        value: products.products
      })
    );
  };
  const removeOtherProducts = useCallback(() => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.multiProductFocus.properties.products",
        value: []
      })
    );
  }, [dispatch]);

  const handleToggleMultiProductFocus = (active: boolean) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.multiProductFocus.active",
        value: active
      })
    );
  };

  const handleUseMultiProductFocusChange = useCallback(
    (use: boolean, products: MultiProducts) => {
      if (isChartFocusActive) {
        handleChartFocusToggle(false);
      }
      handleToggleMultiProductFocus(use);
      if (use) {
        if (products) {
          handleMultiProductFocusChange(products);
        }
      } else {
        removeOtherProducts();
      }
    },
    [
      isChartFocusActive,
      removeOtherProducts,
      handleToggleMultiProductFocus,
      dispatch,
      id,
      handleMultiProductFocusChange
    ]
  );

  return (
    <StyledSpace direction="vertical" split={<StyledDivider />}>
      <HeaderWrapper>
        <Text strong>{getChartFocusText()}</Text>
        <StyledSwitch
          data-testid="chartFocusSettingsSwitch"
          size="small"
          checked={isChartFocusActive}
          onChange={(checked: boolean) => {
            handleChartFocusToggle(checked);
            if (checked) {
              // if multi product focus is active, turn it off
              if (isMultiProductFocusActive) {
                handleToggleMultiProductFocus(false);
              }
              handleLocalBinChange({
                ...localBin,
                UseDynamicBins: userDefaults.binSettings?.useDynamicBins ?? false
              });
            }
          }}
        />
      </HeaderWrapper>
      {isChartFocusActive && (
        <FocusArea>
          <GroupBy
            data-testId="chartFocusGroupBy"
            onChange={handleFocusChange}
            ownsFocus
            value={chartFocusGroupBy}
            //well contribution chart groups by well fields not facility fields
            entityKind={focusEntities}>
            <FocusButton block>
              {chartFocusGroupBy.title}
              <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
            </FocusButton>
          </GroupBy>
          <Popover
            trigger="click"
            placement="topLeft"
            content={
              <FocusBinContainer>
                <BinSettings
                  bin={localBin}
                  entityKind={focusEntities}
                  canEditBin={chartFocusGroupBy.canBin}
                  onReset={
                    localBin.BinType === "Quantile"
                      ? resetQuantileBin
                      : handleResetDefault
                  }
                  onBinChange={(v) => handleLocalBinChange(v)}
                  onBinSettingChange={updateBin}
                  isMinCountWarningVisible={false}
                />
              </FocusBinContainer>
            }>
            <FocusBinButton
              data-testid="chartFocusBinSettings"
              icon={<AllInboxIcon style={{ fontSize: 18 }} />}
            />
          </Popover>
          <ColorPaletteSelector>
            <ColorPaletteListToggle
              activePalette={colorPalette}
              onPaletteSelected={handleColorPaletteSelected}
              showBorder={true}
            />
          </ColorPaletteSelector>
        </FocusArea>
      )}
      {isTotalRateDateChart && (
        <MultiProductFocusSelector
          useMultiProductFocus={isMultiProductFocusActive}
          handleUseMultiProductFocusChange={handleUseMultiProductFocusChange}
          handleMultiProductFocusChange={handleMultiProductFocusChange}
        />
      )}
    </StyledSpace>
  );
};

interface MultiProductFocusSelectorProps {
  useMultiProductFocus: boolean;
  handleUseMultiProductFocusChange?: (use: boolean, products: MultiProducts) => void;
  handleMultiProductFocusChange?: (products: MultiProducts) => void;
}

interface MultiProducts {
  name: string;
  icon: ReactNode;
  tooltipTitle: string;
  products: ("Oil" | "Gas" | "Water Inj" | "Polymer Inj" | "Water")[];
}
const availableMultiProducts: MultiProducts[] = [
  {
    name: "BOE",
    products: ["Oil", "Gas"],
    icon: <ProductBoe6 key="product-boe" />,
    tooltipTitle: "BOE 6:1: Stacked Oil + Cond and Gas"
  },
  {
    name: "Total Fluid",
    products: ["Oil", "Water"],
    icon: <TotalFluid key="total-fluid-product" />,
    tooltipTitle: "Total Fluid: Stacked Oil + Cond and Water"
  },
  {
    name: "Polymer Inj",
    products: ["Polymer Inj", "Water Inj"],
    icon: <PolymerInjection key="product-polymer-injection" />,
    tooltipTitle: "Polymer Inj: Stacked Polymer Injection and Water Injection"
  }
];

const MultiProductFocusSelector: FC<MultiProductFocusSelectorProps> = ({
  useMultiProductFocus,
  handleUseMultiProductFocusChange,
  handleMultiProductFocusChange
}) => {
  const [selectedMultiProduct, setSelectedMultiProduct] = useState<MultiProducts>();
  const renderMultiProducts = (products: MultiProducts[]) => {
    return products.map((product) => (
      <MultiProductItemWrapper key={product.name}>
        <Tooltip title={product.tooltipTitle}>
          <MultiProductButton
            data-testid={product.tooltipTitle}
            icon={product.icon}
            isSelected={selectedMultiProduct === product}
            type="ghost"
            onClick={() => {
              setSelectedMultiProduct(product);
              handleMultiProductFocusChange?.(product);
            }}
          />
        </Tooltip>
      </MultiProductItemWrapper>
    ));
  };

  return (
    <MultiProductRootContainer>
      <HeaderWrapper>
        <Text strong>Multi Product Focus</Text>
        <StyledSwitch
          data-testid="multi-product-focus-switch"
          size="small"
          checked={useMultiProductFocus}
          onChange={(use: boolean) => {
            handleUseMultiProductFocusChange?.(use, selectedMultiProduct);
          }}
        />
      </HeaderWrapper>
      {useMultiProductFocus && (
        <MultiProductItemContainer>
          {renderMultiProducts(availableMultiProducts)}
        </MultiProductItemContainer>
      )}
    </MultiProductRootContainer>
  );
};

const MultiProductRootContainer = styled.div``;

const MultiProductItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding-top: 5px;
`;
interface MultiProductButtonProps {
  isSelected: boolean;
}
const MultiProductButton = styled(IconButton)<MultiProductButtonProps>`
  background-color: ${(props: MultiProductButtonProps) =>
    props.isSelected ? "var(--color-primary)" : "transparent"};
  color: ${(props: MultiProductButtonProps) => (props.isSelected ? "white" : "#a2aaad")};

  &:focus {
    background-color: ${(props: MultiProductButtonProps) =>
      props.isSelected ? "var(--color-primary)" : "transparent"};
    color: ${(props: MultiProductButtonProps) =>
      props.isSelected ? "white" : "#a2aaad"};
  }
`;

const MultiProductItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const HeaderWrapper = styled.div`
  width: 100%;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .ant-switch-checked:focus {
    box-shadow: none;
  }
`;

const StyledSwitch = styled(Switch)`
  margin-left: auto;
`;

const StyledSpace = styled(Space)`
  display: flex;
  min-width: 200px;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const FocusButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FocusArea = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
`;

const FocusBinContainer = styled.div`
  width: 36rem;
  background-color: var(--color-text-06);
  display: grid;
  gap: 1px;
`;

const FocusBinButton = styled(Button)`
  background: transparent;
  color: #a2aaad;
  display: grid;
  justify-content: center;
  align-items: center;

  &:hover {
    color: var(--color-selection);
  }

  &:focus {
    color: var(--color-selection);
  }
`;

const ColorPaletteSelector = styled.div`
  border: none;
  height: 32px;
  width: 32px;

  &:hover {
    border-color: var(--color-selection);
    color: var(--color-selection);
  }

  &:focus {
    border-color: var(--color-selection);
    color: var(--color-selection);
  }
`;
export default ChartFocusSettings;
